import { useId, useState, type KeyboardEvent } from 'react';
import {
  localize,
  type AutomationConditionField,
  type AutomationConditionOperator,
  type LocalizedText,
} from '@neuroedge/shared';
import { Button, Select, TextField } from '../../components/ui';
import { LocalizedTextInput } from '../../components/localized-text/LocalizedTextInput';
import {
  MEDIA_TYPES,
  RULE_KINDS,
  VALUE_LESS_OPERATORS,
  blankRule,
  slugify,
  uniqueId,
  type ActionDraft,
  type RawCondition,
  type ReplyOption,
  type Rule,
  type RuleKind,
} from './automation-model';

/** Building blocks of the simplified automation form (`AutomationFormPage`). */

export const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;
export const box = {
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3)',
  display: 'grid',
  gap: 'var(--space-2)',
} as const;

export function FieldError({ message }: { message?: string }) {
  return message ? (
    <span className="ne-field__error" role="alert">
      {message}
    </span>
  ) : null;
}

export function LinkButton({
  onClick,
  children,
  expanded,
}: {
  onClick: () => void;
  children: React.ReactNode;
  expanded?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      style={{
        all: 'unset',
        cursor: 'pointer',
        color: 'var(--color-primary)',
        fontSize: 'var(--font-size-sm)',
      }}
    >
      {children}
    </button>
  );
}

// --- Keyword chips -----------------------------------------------------------

/** Type a word and press Enter (or comma) to add it as a chip. */
export function KeywordChips({
  label,
  value,
  onChange,
  error,
}: {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  error?: string;
}) {
  const id = useId();
  const [draft, setDraft] = useState('');

  function commit() {
    const words = draft
      .split(',')
      .map((w) => w.trim())
      .filter((w) => w && !value.some((v) => v.toLowerCase() === w.toLowerCase()));
    if (words.length) onChange([...value, ...words]);
    setDraft('');
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className="ne-field">
      <label className="ne-field__label" htmlFor={id}>
        {label}
      </label>
      <div
        className={['ne-input', error ? 'ne-input--error' : ''].filter(Boolean).join(' ')}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)', alignItems: 'center' }}
      >
        {value.map((word) => (
          <span key={word} className="ne-badge" style={{ display: 'inline-flex', gap: 4 }}>
            {word}
            <button
              type="button"
              aria-label={`Remove ${word}`}
              onClick={() => onChange(value.filter((w) => w !== word))}
              style={{ all: 'unset', cursor: 'pointer', fontWeight: 700 }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={id}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commit}
          placeholder={value.length ? 'Add another…' : 'Type a word, press Enter'}
          style={{
            flex: 1,
            minWidth: 140,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'inherit',
            font: 'inherit',
          }}
        />
      </div>
      <span style={muted}>Matches if the message contains any one of these (any case).</span>
      <FieldError message={error} />
    </div>
  );
}

// --- Rules ---------------------------------------------------------------

export function RuleEditor({
  rule,
  onChange,
  onRemove,
  error,
  replyOptions,
}: {
  rule: Rule;
  onChange: (next: Rule) => void;
  onRemove: () => void;
  error?: string;
  replyOptions: { id: string; title: string; from: string }[];
}) {
  const value = rule.keywords[0] ?? '';
  const setValue = (v: string) => onChange({ ...rule, keywords: [v] });
  const kinds = RULE_KINDS.filter((k) => k.addable || k.kind === rule.kind);

  return (
    <div style={box}>
      <Select
        label="Rule"
        value={rule.kind}
        onChange={(e) => onChange(blankRule(e.target.value as RuleKind))}
        options={kinds.map((k) => ({ value: k.kind, label: k.label }))}
      />
      {rule.kind === 'keywords' ? (
        <KeywordChips
          label="Words"
          value={rule.keywords}
          onChange={(keywords) => onChange({ ...rule, keywords })}
          error={error}
        />
      ) : rule.kind === 'media' ? (
        <Select
          label="Type"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          options={MEDIA_TYPES}
          error={error}
        />
      ) : rule.kind === 'button' ? (
        <>
          {replyOptions.length ? (
            <Select
              label="Button / option"
              value={replyOptions.some((o) => o.id === value) ? value : ''}
              onChange={(e) => setValue(e.target.value)}
              options={[
                { value: '', label: 'Choose a button…' },
                ...replyOptions.map((o) => ({
                  value: o.id,
                  label: `${o.title || o.id} — from "${o.from}"`,
                })),
              ]}
            />
          ) : null}
          <TextField
            label={replyOptions.length ? 'Or its reply id' : 'Reply id of the button'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={error}
          />
        </>
      ) : (
        <TextField
          label={rule.kind === 'exact' ? 'Exact message' : 'Text'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={error}
        />
      )}
      <div>
        <Button type="button" variant="secondary" onClick={onRemove}>
          Remove rule
        </Button>
      </div>
    </div>
  );
}

const FIELD_OPTIONS: { value: AutomationConditionField; label: string }[] = [
  { value: 'message.text', label: 'Message text' },
  { value: 'message.replyPayload', label: 'Tapped button / option id' },
  { value: 'message.type', label: 'Message type (TEXT, IMAGE, …)' },
];

const OPERATOR_OPTIONS: { value: AutomationConditionOperator; label: string }[] = [
  { value: 'CONTAINS_ANY', label: 'Contains any of (one per line)' },
  { value: 'EQUALS', label: 'Equals' },
  { value: 'NOT_EQUALS', label: 'Does not equal' },
  { value: 'CONTAINS', label: 'Contains (exact case)' },
  { value: 'NOT_CONTAINS', label: 'Does not contain' },
  { value: 'IS_SET', label: 'Is set' },
  { value: 'IS_NOT_SET', label: 'Is not set' },
];

/** The original OR-groups-of-AND editor, for automations whose rules don't fit the simple model. */
export function AdvancedRulesEditor({
  groups,
  onChange,
  errors,
}: {
  groups: RawCondition[][];
  onChange: (next: RawCondition[][]) => void;
  errors: Record<string, string>;
}) {
  let flat = 0;
  const blank: RawCondition = { field: 'message.text', operator: 'CONTAINS_ANY', value: '' };
  const patch = (gi: number, ri: number, p: Partial<RawCondition>) =>
    onChange(
      groups.map((g, i) => (i === gi ? g.map((r, j) => (j === ri ? { ...r, ...p } : r)) : g)),
    );
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <span style={muted}>
        Each box is one alternative (OR). Every rule inside a box must match (AND).
      </span>
      {groups.map((group, gi) => (
        <div key={gi} style={box}>
          <strong style={{ fontSize: 'var(--font-size-sm)' }}>
            {gi === 0 ? 'Alternative 1' : `OR alternative ${gi + 1}`}
          </strong>
          {group.map((row, ri) => {
            const key = `conditions/${flat}/value`;
            flat += 1;
            return (
              <div
                key={ri}
                style={{
                  display: 'grid',
                  gap: 'var(--space-2)',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  alignItems: 'end',
                }}
              >
                <Select
                  label={ri === 0 ? 'Field' : 'AND field'}
                  value={row.field}
                  onChange={(e) =>
                    patch(gi, ri, { field: e.target.value as AutomationConditionField })
                  }
                  options={FIELD_OPTIONS}
                />
                <Select
                  label="Check"
                  value={row.operator}
                  onChange={(e) =>
                    patch(gi, ri, { operator: e.target.value as AutomationConditionOperator })
                  }
                  options={OPERATOR_OPTIONS}
                />
                {!VALUE_LESS_OPERATORS.has(row.operator) ? (
                  row.operator === 'CONTAINS_ANY' ? (
                    <div className="ne-field">
                      <label className="ne-field__label">Words (one per line)</label>
                      <textarea
                        className="ne-input"
                        rows={2}
                        value={row.value}
                        onChange={(e) => patch(gi, ri, { value: e.target.value })}
                      />
                      <FieldError message={errors[key]} />
                    </div>
                  ) : (
                    <TextField
                      label="Value"
                      value={row.value}
                      onChange={(e) => patch(gi, ri, { value: e.target.value })}
                      error={errors[key]}
                    />
                  )
                ) : null}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    onChange(
                      groups
                        .map((g, i) => (i === gi ? g.filter((_, j) => j !== ri) : g))
                        .filter((g) => g.length > 0),
                    )
                  }
                >
                  Remove
                </Button>
              </div>
            );
          })}
          <div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onChange(groups.map((g, i) => (i === gi ? [...g, { ...blank }] : g)))}
            >
              Add AND rule
            </Button>
          </div>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onChange([...groups, [{ ...blank }]])}
        >
          Add OR alternative
        </Button>
      </div>
    </div>
  );
}

// --- Reply options (buttons / list) ------------------------------------------

function textOf(value: LocalizedText | undefined): string {
  return value === undefined ? '' : localize(value, undefined);
}

/** Every reply id already used by this action (buttons + all list rows). */
function takenIds(a: ActionDraft): string[] {
  return [...a.buttons, ...a.sections.flatMap((s) => s.rows)].map((o) => o.id);
}

/** Re-derives an auto-generated id from a changed label; a hand-edited id is kept. */
function retitle(option: ReplyOption, title: LocalizedText, taken: string[]): ReplyOption {
  const auto = option.id === slugify(textOf(option.title)) || option.id === '';
  if (!auto || !textOf(title).trim()) return { ...option, title };
  return {
    ...option,
    title,
    id: uniqueId(
      slugify(textOf(title)),
      taken.filter((t) => t !== option.id),
    ),
  };
}

export function ReplyOptionsEditor({
  action,
  onChange,
  errorKey,
  errors,
}: {
  action: ActionDraft;
  onChange: (next: ActionDraft) => void;
  errorKey: (field: string) => string;
  errors: Record<string, string>;
}) {
  const [showIds, setShowIds] = useState(false);

  function newOption(base: string, extraTaken: string[] = []): ReplyOption {
    return { id: uniqueId(slugify(base), [...takenIds(action), ...extraTaken]), title: base };
  }

  function setMode(mode: ActionDraft['replyMode']) {
    if (mode === action.replyMode) return;
    const existing = [...action.buttons, ...action.sections.flatMap((s) => s.rows)];
    if (mode === 'buttons') {
      const yes = newOption('Yes');
      onChange({
        ...action,
        replyMode: mode,
        buttons: existing.length ? existing.slice(0, 3) : [yes, newOption('No', [yes.id])],
        sections: [{ rows: [] }],
      });
    } else if (mode === 'list') {
      onChange({
        ...action,
        replyMode: mode,
        buttons: [],
        sections: [{ rows: existing.length ? existing : [newOption('Option 1')] }],
      });
    } else {
      onChange({ ...action, replyMode: mode, buttons: [], sections: [{ rows: [] }] });
    }
  }

  const idHint = (id: string) =>
    showIds ? (
      <span style={muted}>
        Reply id: <code>{id}</code> - use it in another automation's "Customer tapped" rule.
      </span>
    ) : null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <div className="ne-field">
        <span className="ne-field__label">Let them tap a reply</span>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {(
            [
              ['none', 'No'],
              ['buttons', 'Buttons (up to 3)'],
              ['list', 'List (up to 10)'],
            ] as const
          ).map(([mode, label]) => (
            <Button
              key={mode}
              type="button"
              variant={action.replyMode === mode ? 'primary' : 'secondary'}
              onClick={() => setMode(mode)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {action.replyMode === 'buttons' ? (
        <>
          <FieldError message={errors[errorKey('buttons')]} />
          {action.buttons.map((b, bi) => (
            <div key={bi} style={box}>
              <LocalizedTextInput
                label={`Button ${bi + 1}`}
                value={b.title}
                maxLength={20}
                hint="Max 20 characters."
                onChange={(title) =>
                  onChange({
                    ...action,
                    buttons: action.buttons.map((x, j) =>
                      j === bi ? retitle(x, title, takenIds(action)) : x,
                    ),
                  })
                }
              />
              <FieldError message={errors[errorKey(`buttons/${bi}/title`)]} />
              {idHint(b.id)}
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    onChange({ ...action, buttons: action.buttons.filter((_, j) => j !== bi) })
                  }
                >
                  Remove button
                </Button>
              </div>
            </div>
          ))}
          {action.buttons.length < 3 ? (
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  onChange({
                    ...action,
                    buttons: [...action.buttons, newOption(`Option ${action.buttons.length + 1}`)],
                  })
                }
              >
                Add button
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {action.replyMode === 'list' ? (
        <>
          <LocalizedTextInput
            label="List button label"
            value={action.listButtonText}
            maxLength={20}
            hint='The button that opens the list, e.g. "Choose". Max 20 characters.'
            onChange={(listButtonText) => onChange({ ...action, listButtonText })}
          />
          <FieldError message={errors[errorKey('buttonText')]} />
          <FieldError message={errors[errorKey('sections')]} />
          {action.sections.map((section, si) => (
            <div key={si} style={{ display: 'grid', gap: 'var(--space-2)' }}>
              {action.sections.length > 1 ? (
                <strong style={muted}>{textOf(section.title) || `Section ${si + 1}`}</strong>
              ) : null}
              {section.rows.map((row, ri) => {
                const setRow = (next: ReplyOption) =>
                  onChange({
                    ...action,
                    sections: action.sections.map((s, i) =>
                      i === si ? { ...s, rows: s.rows.map((r, j) => (j === ri ? next : r)) } : s,
                    ),
                  });
                return (
                  <div key={ri} style={box}>
                    <LocalizedTextInput
                      label={`Option ${ri + 1}`}
                      value={row.title}
                      maxLength={24}
                      onChange={(title) => setRow(retitle(row, title, takenIds(action)))}
                    />
                    <LocalizedTextInput
                      label="Description (optional)"
                      value={row.description}
                      maxLength={72}
                      onChange={(description) => setRow({ ...row, description })}
                    />
                    {idHint(row.id)}
                    <div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          onChange({
                            ...action,
                            sections: action.sections.map((s, i) =>
                              i === si ? { ...s, rows: s.rows.filter((_, j) => j !== ri) } : s,
                            ),
                          })
                        }
                      >
                        Remove option
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          {action.sections.flatMap((s) => s.rows).length < 10 ? (
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  const count = action.sections.flatMap((s) => s.rows).length;
                  const last = action.sections.length - 1;
                  onChange({
                    ...action,
                    sections: action.sections.map((s, i) =>
                      i === last
                        ? { ...s, rows: [...s.rows, newOption(`Option ${count + 1}`)] }
                        : s,
                    ),
                  });
                }}
              >
                Add option
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {action.replyMode !== 'none' ? (
        <LinkButton onClick={() => setShowIds((s) => !s)} expanded={showIds}>
          {showIds ? '▾' : '▸'} Show reply ids
        </LinkButton>
      ) : null}
    </div>
  );
}
